import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as authHelper from './AuthHelpers';
import { AuthModel, UserModel } from './_models';
import { getUser } from './_requests';
import { socket } from '../socket';
import Loading from '../../components/Loading';

const API_URL = import.meta.env.VITE_APP_API_URL;

type AuthContextProps = {
  auth: AuthModel | undefined;
  saveAuth: (auth: AuthModel | undefined) => void;
  currentUser: UserModel | undefined;
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>;
  logout: () => void;
};

const initAuthContextPropsState: AuthContextProps = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
};

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState);

const useAuth = () => useContext(AuthContext);

interface ProviderProps {
  children: React.ReactNode;
}

const AuthProvider: FC<ProviderProps> = ({ children }) => {
  const { t } = useTranslation(['auth']);
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth());
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>();

  const saveAuth = (authData: AuthModel | undefined) => {
    setAuth(authData);
    if (authData) authHelper.setAuth(authData);
    else authHelper.removeAuth();
  };

  const logout = () => {
    axios.get(`${API_URL}/admin/logout`).finally(() => {
      saveAuth(undefined);
      setCurrentUser(undefined);
    });
  };

  // Inactivity handler: logout after 2 minutes (120000 ms) of inactivity
  useEffect(() => {
    if (!auth) return;

    let timeoutId: ReturnType<typeof setTimeout>;
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        logout();
        toast.info(t('inactivity'));
      }, 2 * 60 * 1000); // 2 minutes
    };

    const activityEvents = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, resetTimer)
    );

    // Start the initial timer
    resetTimer();

    return () => {
      clearTimeout(timeoutId);
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, resetTimer)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  return (
    <AuthContext.Provider
      value={{ auth, saveAuth, currentUser, setCurrentUser, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

const AuthInit: FC<ProviderProps> = ({ children }) => {
  const { auth, currentUser, logout, setCurrentUser } = useAuth();
  const { i18n } = useTranslation();
  const [showSplashScreen, setShowSplashScreen] = useState(true);

  // Request the current user via the auth token (e.g., API_TOKEN) before rendering the app.
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!currentUser) {
          const { data } = await getUser();
          if (data) setCurrentUser(data);
        }
      } catch (error) {
        console.error(error);
        if (currentUser) {
          logout();
        }
      } finally {
        setShowSplashScreen(false);
      }
    };

    if (auth) requestUser();
    else setShowSplashScreen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socket.connect();

    socket.on('connect', () => {
      console.debug('socket connected to server', socket.id);
    });

    socket.on('disconnect', () => {
      console.debug('socket disconnected from server');
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!currentUser) return;

    if (currentUser.language !== i18n.language)
      i18n.changeLanguage(currentUser.language ?? 'en');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  return showSplashScreen ? <Loading /> : <>{children}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
export { AuthProvider, AuthInit, useAuth };
